import { useCallback, useState } from 'react'

import useDebouncedSearch from 'app/common/hooks/useDebouncedSearch'
import ControlsBar, { ControlsBarSpace } from 'app/common/ui-next/controls-bar'
import PlainInputSearch from 'app/common/ui-next/plain-input-search'
import PlainButton from 'app/common/ui-next/plain-button'
import PlainIcon, { iconColor, iconType } from 'app/common/ui-next/plain-icon'
import PlainButtonAsLink from 'app/common/ui-next/plain-button-as-link'
import { useWarehouseStockExportFile } from 'app/dataProviders/warehouse/stock/hooks'
import { BuildingId, OrgId, WarehouseStockItemId } from 'app/common/types/id'
import { useCheckedIds } from 'app/common/templates-next/table-template'

import styles from './WarehouseStockToolbar.module.scss'
import { WarehouseStockUpload } from './WarehouseStockUpload'

export const WarehouseStockToolbar = ({
  onSearch,
  orgId,
  buildingId,
  onRefresh,
}: {
  onSearch: (value: string) => void
  orgId?: OrgId | undefined
  buildingId?: BuildingId | undefined
  onRefresh?: () => void
}) => {
  const resetSearch = useCallback(() => onSearch(''), [onSearch])
  const [searchString, search, clearSearch] = useDebouncedSearch(onSearch, resetSearch)

  const checkedIds = useCheckedIds()

  const [exporting, setExporting] = useState(false)

  const { download } = useWarehouseStockExportFile()

  const handleDownload = useCallback(() => {
    setExporting(true)
    download({
      orgId,
      buildingId,
      search: searchString,
      ids: checkedIds as Array<WarehouseStockItemId>,
    }).finally(() => setExporting(false))
  }, [download, orgId, buildingId, searchString, checkedIds])

  return (
    <ControlsBar>
      <PlainInputSearch
        className={styles.search}
        id="search"
        placeholder="Поиск"
        value={searchString}
        onChange={search}
        clearSearch={clearSearch}
        autoFocus
      />

      <ControlsBarSpace />

      <PlainButtonAsLink onClick={handleDownload} disabled={exporting}>
        <PlainIcon
          className={styles.icon}
          type={iconType.EXCEL_MONO}
          color={exporting ? iconColor.COLOR_GRAY : iconColor.COLOR_BUTTON_PRIMARY}
        />
      </PlainButtonAsLink>

      <WarehouseStockUpload onSuccess={onRefresh}>
        {({ isUploading, handleButtonClick, uploadProgress }) => (
          <PlainButton onClick={handleButtonClick} disabled={isUploading}>
            {isUploading ? `Загрузка... ${uploadProgress}%` : 'Загрузить шаблон'}
          </PlainButton>
        )}
      </WarehouseStockUpload>
    </ControlsBar>
  )
}
